import fetch from 'portable-fetch';
import { Hosts } from './host';
import { PagedResponse } from '@densityco/lib-api-types';

import { CaptureScheduleRequest } from './calculate-capture-schedules';
import {
  MultiPointInstallation,
  Sensor,
  Organization,
  Capture,
  CaptureGroup,
  Note,
  NoteAction,
} from '../types/api';

import { rxDispatch } from '../rx-stores';
import { SessionActionTypes } from '../types/session';
import EnvironmentStore from '../rx-stores/environment';
import { Toaster } from '@densityco/ui';

const formatAuthorizationToken = (token) => {
  if (token.startsWith('ses_')) {
    return `Bearer ${token}`;
  }
  return `jwt ${token}`;
};

type QueryStringParams = { [key: string]: string | number | boolean };

const formatQueryString = (params: QueryStringParams): string => {
  return new URLSearchParams(
    Object.fromEntries(
      Object.entries(params)
        .filter(
          ([k, v]) => typeof k !== 'undefined' && typeof v !== 'undefined',
        )
        .map(([k, v]) => {
          // Convert number and boolean values into strings
          if (typeof v === 'number' || typeof v === 'boolean') {
            return [k, `${v}`];
          } else {
            return [k, v];
          }
        }),
    ),
  ).toString();
};

// After each request, check to see if the request failed due to auth reasons, and if it did, then redirect back to the login page.
const handleAuthErrors = (response: Response) => {
  if (response.status === 403 && !window.location.href.includes('#/login')) {
    // Redirect to login page, including a `next` value
    const next = window.location.hash.replace(/^#/, '');
    if (next.length > 0) {
      window.location.hash = `#/login?next=${encodeURIComponent(next)}`;
    } else {
      window.location.href = '#/login';
    }

    // After the redirect has happened, show a toast explaining why
    setTimeout(() => {
      rxDispatch({ type: SessionActionTypes.SESSION_CLEAR_TOKEN });
      Toaster.showToast({
        text: 'Your session has expired, please log in again.',
      });
    }, 1000);
  }
};

export const SenseiApi = {
  async organizationSettings(accessToken, organizationId) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/organization/${organizationId}/settings`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get organization settings: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async sensorList(accessToken, querystring) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors?${formatQueryString(querystring)}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get sensors: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async sensorDetails(accessToken, id) {
    const response = await fetch(`${Hosts.SenseiApiHost}/sensors/${id}`, {
      method: 'GET',
      headers: {
        Authorization: formatAuthorizationToken(accessToken),
      },
    });
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get sensor with serial number ${id}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async enableSensorPriorityAnnotation(
    accessToken,
    serialNumbers: Array<string>,
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/prioritize-captures`,
      {
        method: 'POST',
        body: JSON.stringify({ serial_numbers: serialNumbers }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );

    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Error setting capture priority for sensors: ${
          response.status
        } ${await response.text()}`,
      );
    }
  },
  async spaceList(accessToken, filters) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/spaces?${formatQueryString(filters)}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get spaces: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async spaceDetails(accessToken, id) {
    const response = await fetch(`${Hosts.SenseiApiHost}/spaces/${id}`, {
      method: 'GET',
      headers: {
        Authorization: formatAuthorizationToken(accessToken),
      },
    });
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get space with id ${id}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async spaceCounts(accessToken, id, org_id, startTime, endTime) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/spaces/${id}/counts?start=${startTime}&end=${endTime}&org_id=${org_id}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get counts for space with id ${id}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async organizationList(accessToken, params = {}) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/organizations?${formatQueryString(params)}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get organizations: ${await response.text()}`,
      );
    }
    return (await response.json()).results;
  },
  async sensorCaptureList(accessToken, id, filters = {}) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/${id}/captures?${formatQueryString(
        filters,
      )}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get captures for sensor with serial number ${id}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async captureQuickAccuracy(
    accessToken,
    id,
    config,
    primary: boolean = false,
  ) {
    let url = `${Hosts.SenseiApiHost}/captures/${id}/report/accuracy`;
    if (primary) {
      url += '?primary=true';
    }
    const response = await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ config: config }),
      headers: {
        Authorization: formatAuthorizationToken(accessToken),
      },
    });
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not create accuracy report for capture with id ${id}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async updateCaptureAnnotation(accessToken, annotation) {
    let url = `${Hosts.SenseiApiHost}/annotations/${annotation.id}`;
    const response = await fetch(url, {
      method: 'PUT',
      body: JSON.stringify(annotation),
      headers: {
        Authorization: formatAuthorizationToken(accessToken),
      },
    });
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(`Error updating annotation: ${await response.text()}`);
    }
    return response;
  },
  async putSensorConfiguration(
    accessToken,
    sensor_serial,
    config,
    message: string | null = null,
    reannotateCaptures = false,
  ) {
    const queryParams = new URLSearchParams();
    if (message !== null) {
      queryParams.set('message', message);
    }
    queryParams.set(
      'resend_captures_for_annotation',
      reannotateCaptures ? 'true' : 'false',
    );

    const response = await fetch(
      `${
        Hosts.SenseiApiHost
      }/sensors/${sensor_serial}/config?${queryParams.toString()}`,
      {
        method: 'PUT',
        body: JSON.stringify(config),
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not put configuration for sensor ${sensor_serial}: ${await response.text()}`,
      );
    }
  },
  async listNotes(accessToken: string, externalId: string) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/notes/${externalId}?page_size=5000`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get notes for resource ${externalId}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async createNote(
    accessToken: string,
    externalId: string,
    type: string,
    data: object,
  ) {
    const response = await fetch(`${Hosts.SenseiApiHost}/notes/${externalId}`, {
      method: 'POST',
      headers: {
        Authorization: formatAuthorizationToken(accessToken),
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ type, data }),
    });
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not create note for resource ${externalId}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async updateNote(
    accessToken: string,
    externalId: string,
    id: number,
    data: object,
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/notes/${externalId}/${id}`,
      {
        method: 'PUT',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ data }),
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not update note ${id} on resource ${externalId}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async deleteNote(accessToken: string, externalId: string, id: number) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/notes/${externalId}/${id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not delete note ${id} on resource ${externalId}: ${await response.text()}`,
      );
    }
  },
  async createCaptureSchedules(
    accessToken,
    organizationId: Organization['id'],
    data: Array<CaptureScheduleRequest>,
  ) {
    const response = await fetch(`${Hosts.SenseiApiHost}/capture_schedules`, {
      method: 'POST',
      body: JSON.stringify({
        organization_id: organizationId,
        capture_schedules: data.map((i) => i.body),
      }),
      headers: {
        Authorization: formatAuthorizationToken(accessToken),
      },
    });

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error creating capture schedule: ${
          response.status
        } ${await response.text()}`,
      );
    }
  },
  async triggerCapture(
    accessToken,
    ids: Array<Sensor['serial_number']>,
    options: {
      duration: number;
      motion: boolean;
      motion_detection_trigger_line: boolean;
      dense_capture: boolean;
      number_of_captures: number;
      max_capture_duration: number;
    },
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/trigger-captures`,
      {
        method: 'POST',
        body: JSON.stringify({ ids, ...options }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error triggering captures: ${
          response.status
        } ${await response.text()}`,
      );
    }
  },
  async stopCapture(accessToken, ids: Array<Sensor['serial_number']>) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/stop-captures`,
      {
        method: 'POST',
        body: JSON.stringify({ ids }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error stopping captures: ${response.status} ${await response.text()}`,
      );
    }
  },
  async getCaptureDetails(accessToken, captureId: number) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/captures/${captureId}/details`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get captures for capture id ${captureId}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async deleteCaptures(accessToken, captureIds: Array<Capture['id']>) {
    const response = await fetch(`${Hosts.SenseiApiHost}/captures`, {
      method: 'DELETE',
      headers: {
        Authorization: formatAuthorizationToken(accessToken),
      },
      body: JSON.stringify({ capture_ids: captureIds }),
    });
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not delete capture ids ${captureIds.join(
          ', ',
        )}: ${await response.text()}`,
      );
    }
  },
  async sendCapturesForReannotation(
    accessToken,
    captureIds: Array<Capture['id']>,
    reannotation_reason: string = '',
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/captures/send-for-reannotation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: formatAuthorizationToken(accessToken),
        },
        body: JSON.stringify({
          capture_ids: captureIds,
          reannotation_reason: reannotation_reason.toLowerCase(),
        }),
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not send captures for reannotation: ${await response.text()}`,
      );
    }
  },
  async measureHeight(accessToken, serialNumber: Sensor['serial_number']) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/${serialNumber}/measure-height`,
      {
        method: 'POST',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );

    handleAuthErrors(response);
    if (response.ok) {
      return response.json();
    } else {
      if (response.status === 400) {
        const data = await response.json();
        let details: string;
        if ('details' in data) {
          details = data.details;
        } else {
          details = JSON.stringify(data);
        }
        throw new Error(`Error triggering measure height command: ${details}`);
      } else if (response.status >= 500) {
        throw new Error(
          `Error triggering measure height command: there's an issue with the server (error code ${response.status}). Please try again later.`,
        );
      } else {
        // These are unexpected so just display whatever we can
        throw new Error(
          `Error triggering measure height command: ${
            response.status
          } ${await response.text()}`,
        );
      }
    }
  },
  async listCommands(accessToken, serialNumber: Sensor['serial_number']) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/${serialNumber}/commands`,
      {
        headers: {
          Accept: 'application/json',
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );

    handleAuthErrors(response);
    if (response.ok) {
      return response.json();
    } else {
      throw new Error(
        `Error listing sensor commands: ${
          response.status
        } ${await response.text()}`,
      );
    }
  },
  async rmaReset(accessToken, serialNumbers: Array<Sensor['serial_number']>) {
    const response = await fetch(`${Hosts.SenseiApiHost}/sensors/rma-reset`, {
      method: 'POST',
      body: JSON.stringify({ serial_numbers: serialNumbers }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: formatAuthorizationToken(accessToken),
      },
    });

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error RMA Resetting sensors: ${
          response.status
        } ${await response.text()}`,
      );
    }
  },
  async reboot(accessToken, serialNumbers: Array<Sensor['serial_number']>) {
    const response = await fetch(`${Hosts.SenseiApiHost}/sensors/reboot`, {
      method: 'POST',
      body: JSON.stringify({ serial_numbers: serialNumbers }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: formatAuthorizationToken(accessToken),
      },
    });

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error rebooting sensors: ${response.status} ${await response.text()}`,
      );
    }
  },
  async locate(accessToken, serialNumber: Sensor['serial_number']) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/${serialNumber}/locate`,
      {
        method: 'POST',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error locating sensor: ${response.status} ${await response.text()}`,
      );
    }
  },
  async recalibrate(
    accessToken,
    serialNumbers: Array<Sensor['serial_number']>,
    message: string | null = null,
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/recalibrate?message=${
        message ? encodeURIComponent(message) : ''
      }`,
      {
        method: 'POST',
        body: JSON.stringify({ serial_numbers: serialNumbers }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error recalibrating sensors: ${
          response.status
        } ${await response.text()}`,
      );
    }
  },
  async uploadLogs(accessToken, serialNumbers: Array<Sensor['serial_number']>) {
    const response = await fetch(`${Hosts.SenseiApiHost}/sensors/upload-logs`, {
      method: 'POST',
      body: JSON.stringify({ serial_numbers: serialNumbers }),
      headers: {
        'Content-Type': 'application/json',
        Authorization: formatAuthorizationToken(accessToken),
      },
    });

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error uploading logs: ${response.status} ${await response.text()}`,
      );
    }
  },
  async factoryReset(
    accessToken,
    serialNumbers: Array<Sensor['serial_number']>,
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/factory-reset`,
      {
        method: 'POST',
        body: JSON.stringify({ serial_numbers: serialNumbers }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );

    handleAuthErrors(response);
    if (response.ok) {
      return;
    } else {
      throw new Error(
        `Error factory resetting: ${response.status} ${await response.text()}`,
      );
    }
  },
  async search(accessToken, searchQuery: string) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/search?q=${encodeURIComponent(searchQuery)}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not perform search: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async multiPointInstallationList(
    accessToken: string,
    params: QueryStringParams,
  ): Promise<PagedResponse<MultiPointInstallation>> {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations?${formatQueryString(
        params,
      )}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get multi point installations: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async multiPointInstallationDetail(
    accessToken: string,
    id: string,
  ): Promise<MultiPointInstallation> {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations/${id}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get multi point installation ${id}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async multiPointInstallationCreate(
    accessToken: string,
    organizationId: Organization['id'],
    body: MultiPointInstallation,
  ): Promise<MultiPointInstallation> {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations?organization_id=${organizationId}`,
      {
        method: 'POST',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
        body: JSON.stringify(body),
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      // Attempt to locate an error message in the response body
      let jsonBody = null;
      try {
        jsonBody = await response.json();
      } catch (error) {
        throw new Error('Error creating MPI!');
      }

      if (jsonBody && jsonBody.detail) {
        throw new Error(jsonBody.detail);
      } else {
        throw new Error('Error creating MPI!');
      }
    }
    return response.json();
  },
  async multiPointInstallationDelete(
    accessToken: string,
    id: string,
  ): Promise<void> {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations/${id}`,
      {
        method: 'DELETE',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not delete multi point installation ${id}: ${await response.text()}`,
      );
    }
  },
  async multiPointInstallationUpdate(
    accessToken: string,
    mpiId: string,
    body: {
      name?: string;
      sensor_height?: number;
      sensors?: Array<{
        sensor_serial: string;
        spacing_right: number;
      }>;
      config?: {
        span_ms: number;
        max_x_offset_multiplier: number;
      };
    },
  ): Promise<void> {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations/${mpiId}`,
      {
        method: 'PUT',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
        body: JSON.stringify(body),
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not update multi point installation ${mpiId}: ${await response.text()}`,
      );
    }
  },
  async multiPointInstallationCaptureGroups(
    accessToken: string,
    mpiId: string,
    params: QueryStringParams,
  ) {
    const response = await fetch(
      `${
        Hosts.SenseiApiHost
      }/multipoint_installations/${mpiId}/capture_groups?${formatQueryString(
        params,
      )}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get capture groups for multi point installation ${mpiId}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async multiPointInstallationCaptureGroupsDelete(
    accessToken: string,
    mpiId: string,
    captureGroupIds: Array<CaptureGroup['id']>,
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations/${mpiId}/capture_groups`,
      {
        method: 'DELETE',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
        body: JSON.stringify({ capture_group_ids: captureGroupIds }),
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get delete capture groups for multi point installation ${mpiId}: ${await response.text()}`,
      );
    }
  },
  async multiPointInstallationCaptureGroup(
    accessToken: string,
    mpiId: string,
    groupId: number,
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations/${mpiId}/capture_groups/${groupId}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get capture groups for multi point installation ${mpiId}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async multiPointInstallationM2MCaptureGroups(
    accessToken: string,
    mpiId: string,
    params: QueryStringParams,
  ) {
    const response = await fetch(
      `${
        Hosts.SenseiApiHost
      }/multipoint_installations/${mpiId}/m2m_capture_groups?${formatQueryString(
        params,
      )}`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get capture groups for multi point installation ${mpiId}: ${await response.text()}`,
      );
    }
    return response.json();
  },

  async multiPointInstallationAccuracy(accessToken: string, mpiId: string) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations/${mpiId}/accuracy`,
      {
        method: 'GET',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get accuracy for multi point installation ${mpiId}: ${await response.text()}`,
      );
    }
    return response.json();
  },
  async multiPointInstallationM2MCaptureGroupsDelete(
    accessToken: string,
    mpiId: string,
    captureGroupIds: Array<CaptureGroup['id']>,
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/multipoint_installations/${mpiId}/m2m_capture_groups`,
      {
        method: 'DELETE',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
        body: JSON.stringify({ capture_group_ids: captureGroupIds }),
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not get delete capture groups for multi point installation ${mpiId}: ${await response.text()}`,
      );
    }
  },
  async performNoteAction(
    accessToken,
    externalId: string,
    noteId: Note['id'],
    noteActionId: NoteAction['id'],
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/notes/${externalId}/${noteId}/actions/${noteActionId}`,
      {
        method: 'POST',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not perform note action ${noteActionId} for note ${noteId}: ${await response.text()}`,
      );
    }

    return response.json();
  },
  async transitionSensorState(
    accessToken,
    serialNumber: string,
    afterState: Sensor['current_state'],
  ) {
    const response = await fetch(
      `${Hosts.SenseiApiHost}/sensors/${serialNumber}/transition`,
      {
        method: 'POST',
        headers: {
          Authorization: formatAuthorizationToken(accessToken),
        },
        body: JSON.stringify({
          after_state: afterState,
        }),
      },
    );
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not perform state transition for ${serialNumber}: ${await response.text()}`,
      );
    }
  },
  async listSensorStates(accessToken: string): Promise<{
    results: Array<string>;
    human_readable_state_names: { [state: string]: string };
  }> {
    const response = await fetch(`${Hosts.SenseiApiHost}/sensors/states`, {
      method: 'GET',
      headers: {
        Authorization: formatAuthorizationToken(accessToken),
      },
    });
    handleAuthErrors(response);
    if (!response.ok) {
      throw new Error(
        `Sensei API could not list all sensor states: ${await response.text()}`,
      );
    }
    return response.json();
  },
};

export const ErrorCollector = {
  async send(data: string) {
    const response = await fetch(`${Hosts.ErrorCollectorHost}/errors/sensei`, {
      method: 'POST',
      body: data,
    });
    if (!response.ok) {
      throw new Error(
        `Sensei API could not log an error to the error collector: ${await response.text()}`,
      );
    }
  },
};
